import {
  ENTITLEMENT_EVENT_TYPES,
  PAYMENT_EVENT_TYPES,
  SUBSCRIPTION_EVENT_TYPE_NAMES,
  TRIAL_EVENT_TYPES,
} from '../constants';
import type { SubscriptionEventAnalytics, SubscriptionEventKpis } from '../types';

export function formatDateTime(value: string | null | undefined, locale: string): string {
  if (!value) return '—';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '—';
  return date.toLocaleString(locale, {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
}

export function formatShortId(value: string | null | undefined): string | null {
  const trimmed = value?.trim();
  if (!trimmed) return null;
  if (trimmed.length <= 12) return trimmed;
  return `${trimmed.slice(0, 8)}…${trimmed.slice(-4)}`;
}

export function sumEventCounts(
  eventsByType: Record<string, number>,
  types: readonly string[],
): number {
  return types.reduce((sum, type) => sum + (eventsByType[type] ?? 0), 0);
}

export function computeSubscriptionEventKpis(
  analytics: SubscriptionEventAnalytics | undefined,
): SubscriptionEventKpis {
  if (!analytics) {
    return {
      totalEvents: 0,
      trialEvents: 0,
      subscriptionEvents: 0,
      paymentEvents: 0,
      entitlementEvents: 0,
      last30DaysEvents: 0,
    };
  }

  const { eventsByType, totalEvents, last30Days } = analytics;

  return {
    totalEvents,
    trialEvents: sumEventCounts(eventsByType, TRIAL_EVENT_TYPES),
    subscriptionEvents: sumEventCounts(eventsByType, SUBSCRIPTION_EVENT_TYPE_NAMES),
    paymentEvents: sumEventCounts(eventsByType, PAYMENT_EVENT_TYPES),
    entitlementEvents: sumEventCounts(eventsByType, ENTITLEMENT_EVENT_TYPES),
    last30DaysEvents: last30Days.totalEvents,
  };
}

export function recordToChartData(
  record: Record<string, number>,
): Array<{ name: string; count: number }> {
  return Object.entries(record)
    .map(([name, count]) => ({ name, count }))
    .sort((a, b) => b.count - a.count || a.name.localeCompare(b.name));
}

export async function copyToClipboard(text: string): Promise<boolean> {
  try {
    await navigator.clipboard.writeText(text);
    return true;
  } catch {
    return false;
  }
}

export function formatMetadataJson(metadata: unknown): string {
  if (metadata === null || metadata === undefined) return '';
  try {
    return JSON.stringify(metadata, null, 2);
  } catch {
    return String(metadata);
  }
}

export function resolveQueryErrorMessage(
  error: unknown,
  t: (key: string) => string,
  isApiErrorFn: (err: unknown) => err is { status: number },
): string {
  if (isApiErrorFn(error)) {
    if (error.status === 403) return t('subscriptionEvents.errors.forbidden');
    if (error.status === 401) return t('subscriptionEvents.errors.unauthorized');
  }
  return t('subscriptionEvents.errors.loadFailed');
}
